import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";

import {
  getHistoryRowsData,
  isAutoSave,
  mapHistory,
} from "./HistoryModal.utils";
import { getYachtId } from "src/utils";
/**
 * Custom hook to manage the state and behavior of the History Modal.
 *
 * @param requestBody - An optional object representing the request body for fetching history data.
 * @param isMedStat - An optional boolean indicating if the modal is used for medical statements.
 * @returns An object containing:
 * - `showAutoSaves`: A boolean indicating if auto-saved entries are being shown.
 * - `history`: The processed history data to be displayed in the modal.
 * - `toggleAutoSaves`: A function to toggle the visibility of auto-saved entries.
 * - `autoSavesNumber`: The number of auto-saved entries.
 * - `handleViewHistory`: A function to navigate to a specific history entry.
 * - `isLoading`: A boolean indicating if the history data is being loaded.
 * - `refetch`: A function to refetch the history data.
 */
export const useHistoryModal = (requestBody?: any, isMedStat?: boolean) => {
  const [showAutoSaves, setShowAutoSaves] = useState(false);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const { lbeidOrPartId, userid } = useParams();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const getHistoryParams = useCallback(() => {
    return { yachtid: getYachtId(), id: lbeidOrPartId ?? id };
  }, [lbeidOrPartId, id]);

  const body = requestBody ?? {
    request: "get-entry-history",
    body: { params: getHistoryParams() },
  };
  const {
    data: _history,
    isLoading,
    refetch,
  } = useGetApi<any[]>({
    ...body,
    mapper: mapHistory,
  });
  const handleViewHistory = useCallback(
    (hid?: string) => {
      if (!hid) return;
      return () => {
        setSearchParams(
          prev => {
            prev.set("fromHistory", "true");
            return prev;
          },
          { replace: true },
        );
        if (isMedStat && userid) {
          navigate(
            `/settings/yacht/medlog/view-history/${userid}/medical-statements/history/${hid}`,
          );
          return;
        }
        navigate(`/logbook/${hid}/history`);
      };
    },
    [isMedStat, navigate, setSearchParams, userid],
  );

  const toggleAutoSaves = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setShowAutoSaves(event.currentTarget.checked);
    },
    [],
  );

  const history = useMemo(
    () =>
      _history &&
      getHistoryRowsData(
        showAutoSaves || isMedStat
          ? _history
          : _history.filter((h: any) => !isAutoSave(h)),
      ),
    [_history, isMedStat, showAutoSaves],
  );
  const autoSavesNumber = useMemo(
    () => _history?.filter(isAutoSave).length ?? 0,
    [_history],
  );

  return {
    showAutoSaves,
    history,
    toggleAutoSaves,
    autoSavesNumber,
    handleViewHistory,
    isLoading,
    refetch,
  };
};
